import axios from "axios";
import { backendUrl } from "../Router/constant";

// REACT_APP_BACKEND_URL already ends with /api/, so it is used as-is.
const axionIns = axios.create({
  baseURL: backendUrl,
  // don't hardcode Content-Type here
});

// request interceptor
axionIns.interceptors.request.use(function (config) {
  const token = localStorage.getItem("authToken");

  // A few calls are deliberately unauthenticated. Completing a checkout is the
  // one that matters: the checkout token is the proof, and the browser may be
  // carrying a token for a different account - the public checkout page is
  // reachable while signed in - which the backend should not have to reason
  // about.
  if (token && !config.skipAuth) {
    config.headers["token"] = token;
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  // If sending FormData, let the browser set the correct multipart boundary
  if (config.data instanceof FormData) {
    // remove any manually set content-type so axios can handle it
    delete config.headers["Content-Type"];
  } else {
    // for normal JSON requests, set application/json
    config.headers["Content-Type"] = "application/json";
  }

  return config;
});

// A paused account - an unpaid renewal, or a cancelled plan whose period has
// run out - answers 402 on every owner API except /auth/me, /account/*,
// /billing/* and /notifications/*. Each page would otherwise show its own
// "something went wrong"; instead the app says so once and sends the owner to
// Billing, where the way out is.
//
// Announced as an event rather than navigating from here: this module has no
// router, and a hard redirect would throw away everything else on the page.
export const PAUSED_EVENT = "hostor:account-paused";

// The session is gone - expired, signed out elsewhere, or never issued (the
// public checkout creates the account without one). Any request can be the one
// that finds out, and the API says so in two different ways: a 401, or a 200
// carrying { status: false, error: "Not signed in" }. Both end up here so the
// app asks for a sign-in once, instead of every panel on the page showing
// "Not signed in" on its own.
export const SIGNED_OUT_EVENT = "hostor:signed-out";

const NOT_SIGNED_IN = /not signed in|unauthorized|unauthenticated/i;

// Signing in, registering and verifying all answer 401 for an ordinary wrong
// password or a used code. Those are answers to a form, not an ended session,
// so they never announce one. /auth/me is the session check itself and does.
const isCredentialCall = (url = "") =>
  url.includes("/auth/") && !url.includes("/auth/me");

const announceSignedOut = (config, message) => {
  if (isCredentialCall(config?.url)) return;

  window.dispatchEvent(
    new CustomEvent(SIGNED_OUT_EVENT, { detail: { message } }),
  );
};

axionIns.interceptors.response.use(
  (response) => {
    // A 2xx that says the request was not authenticated. The envelope is
    // { status: false, error }, so the HTTP code alone cannot be trusted.
    const body = response?.data;

    if (body?.status === false && NOT_SIGNED_IN.test(body?.error || "")) {
      announceSignedOut(response.config, body.error);
    }

    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const code = data?.code;

    if (status === 402 && (code === "payment_required" || code === "subscription_ended")) {
      window.dispatchEvent(
        new CustomEvent(PAUSED_EVENT, {
          detail: { code, message: data?.error || "" },
        }),
      );
    }

    if (status === 401) {
      announceSignedOut(error.config, data?.error || "Your session has ended.");
    }

    // Rejected onwards exactly as before: every caller already reads the
    // response off the error through normalizeError().
    return Promise.reject(error);
  },
);

export default axionIns;
